/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { AppService } from '@services/app/app.service';
import { HttpClient } from '@angular/common/http';
import { LabelItemDTO } from '@api/models/label-item.dto';
import { mergeMap, map } from 'rxjs/operators';
import { ApiService } from '@api/services/api/api.service';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import KeyCutter from './key-cutter';

@Injectable({
  providedIn: 'root'
})
export class LabelsService {
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private appService: AppService,
    private translate: TranslateService
  ) {}

  /**
   * carica le labels pubbliche
   */
  loadOpenLabels(): Observable<void> {
    const url = this.appService.getConfig().labels.openLabelsUrl;

    if (!url) {
      return of([]).pipe(map(() => {}));
    }

    return this.http.get<LabelItemDTO[]>(url).pipe(
      map(res => this.listToMap(res || [])),
      mergeMap(labels => this.setTranslation(labels)),
      map(() => {})
    );
  }

  /**
   * carica le labels
   */
  loadLabels(): Observable<void> {
    return this.apiService.getLabels().pipe(
      map(res => this.listToMap(res.labels || [])),
      mergeMap(labels => this.setTranslation(labels)),
      map(() => {})
    );
  }

  /**
   * restituisce traduzione per la key richiesta;
   * assicurarsi di usare il metodo quando le label sono caricate
   */
  getLabel(key: string, interpolateParams?: { [key: string]: string }): string {
    return this.translate.instant(key, interpolateParams);
  }

  /**
   * controlla se la label richiesta esiste
   */
  doesLabelExists(key: string): boolean {
    return this.getLabel(key) !== key;
  }

  /**
   * imposta le traduzioni in base alle label caricate
   */
  private setTranslation(labelsObj: {
    [key: string]: string;
  }): Observable<boolean> {
    const lang = this.getUserLang();

    this.translate.setTranslation(lang, labelsObj, true);
    this.translate.setDefaultLang(lang);

    return this.translate.use(lang).pipe(map(() => true));
  }

  /**
   * converte la lista di label in una mappa
   */
  private listToMap(list: LabelItemDTO[]): { [key: string]: string } {
    const labelMap = {};

    if (!Array.isArray(list)) {
      return labelMap;
    }

    const filter = this.appService.getConfig().labels.filter;
    const filterKey = filter && filter.key;
    const filterVal = filter && filter.value;

    list.forEach(label => {
      const keyCutter = new KeyCutter(label.key);
      const cleanKey = keyCutter.getClean();
      const attrVal = keyCutter.getAttribute(filterKey);

      // considero solo la label che sono compatibili con il filtro impostato;
      // in caso ci sia una stessa label con filtro e senza filtro, prediligo quella con filtro
      if (
        (!filterVal && !attrVal) ||
        (!!filterVal && attrVal === filterVal) ||
        (!!filterVal && !attrVal && !labelMap[cleanKey])
      ) {
        labelMap[cleanKey] = label.value;
      }
    });

    return labelMap;
  }

  /**
   * definisce lal lingua impostata dall'utente
   */
  private getUserLang(): string {
    return this.translate.getBrowserCultureLang();
  }
}
